import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { TableLoaderRow } from "@/components/ui/loader";
import { useDepreciationSchedule } from "@/features/finance/api";
import { currentFy, fyOptions } from "@/features/finance/fy";
import { inr } from "@/features/finance/format";
import { ReportExportButton } from "@/features/reports/export/ReportExportButton";
import { useBranch } from "@/context/BranchContext";
import { ArrowLeft, Search, X, Layers, Landmark, TrendingDown, ShieldAlert } from "lucide-react";

export const Route = createFileRoute("/_authenticated/finance/depreciation-schedule")({
  component: DepreciationSchedulePage,
});

const BOOK_OPTIONS = [
  { value: "FINANCIAL",  label: "Financial" },
  { value: "INCOME_TAX", label: "Income Tax (IT Act)" },
  { value: "MANAGEMENT", label: "Management" },
];

function DepreciationSchedulePage() {
  const [fy, setFy]         = useState(currentFy());
  const [book, setBook]     = useState("FINANCIAL");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("ALL");

  const { currentBranchId } = useBranch();
  const { data, isLoading } = useDepreciationSchedule(fy, book, currentBranchId);
  const rows = data ?? [];

  // Extract distinct category names
  const categoryOptions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => {
      if (r.category_name) set.add(r.category_name);
    });
    return Array.from(set).sort();
  }, [rows]);

  // Filter rows based on search and category
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const matchSearch =
        !q ||
        r.asset_code.toLowerCase().includes(q) ||
        r.asset_name.toLowerCase().includes(q) ||
        (r.category_name ?? "").toLowerCase().includes(q);

      const matchCategory =
        categoryFilter === "ALL" || r.category_name === categoryFilter;

      return matchSearch && matchCategory;
    });
  }, [rows, search, categoryFilter]);

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (a, r) => ({
        gross:    a.gross    + r.gross_block,
        depOpen:  a.depOpen  + r.accum_dep_opening,
        depFy:    a.depFy    + r.dep_this_fy,
        depClose: a.depClose + r.accum_dep_closing,
        nbv:      a.nbv      + r.nbv_closing,
      }),
      { gross: 0, depOpen: 0, depFy: 0, depClose: 0, nbv: 0 }
    );
  }, [filteredRows]);

  const hasFilters = search.trim().length > 0 || categoryFilter !== "ALL";

  return (
    <div className="space-y-4">
      {/* Back navigation & Page Header */}
      <div>
        <Button
          variant="ghost"
          size="sm"
          asChild
          className="mb-2 -ml-2 h-8 gap-1.5 text-muted-foreground hover:text-foreground"
        >
          <Link to="/finance/dashboard">
            <ArrowLeft className="h-4 w-4" />
            Back to Finance
          </Link>
        </Button>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Depreciation Schedule</h1>
            <p className="text-sm text-muted-foreground">
              Asset-wise depreciation summary &middot; FY {fy} &middot; {rows.length} assets capitalised
            </p>
          </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={fy} onValueChange={setFy}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {fyOptions().map((y) => (
                <SelectItem key={y} value={y}>FY {y}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={book} onValueChange={setBook}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BOOK_OPTIONS.map((b) => (
                <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <ReportExportButton
            reportKey="R-DEP-SCH"
            title={`Depreciation Schedule FY ${fy}`}
            filename={`depreciation-schedule-${fy}-${book}`}
            rows={() =>
              filteredRows.map((r) => ({
                "Asset Code":           r.asset_code,
                "Asset Name":           r.asset_name,
                "Category":             r.category_name ?? "",
                "Cap Date":             r.capitalisation_date,
                "Method":               r.method,
                "Rate (%)":             r.dep_rate,
                "Gross Block":          r.gross_block,
                "Acc Dep Opening":      r.accum_dep_opening,
                "Dep This FY":          r.dep_this_fy,
                "Acc Dep Closing":      r.accum_dep_closing,
                "NBV Closing":          r.nbv_closing,
                "Remaining Life (yrs)": r.remaining_life_years ?? "",
              }))
            }
            requiredColumns={["Asset Code"]}
            variant="outline"
            disabled={!filteredRows.length}
          />
        </div>
      </div>
    </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="py-2">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">Assets in Schedule</span>
              <Layers className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-xl font-bold mt-1">
              {filteredRows.length}
              {hasFilters && rows.length !== filteredRows.length && (
                <span className="text-xs font-normal text-muted-foreground ml-1.5">
                  of {rows.length}
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="py-2">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">Total Gross Block</span>
              <Landmark className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-xl font-bold mt-1 text-slate-800 dark:text-slate-100">
              {inr(totals.gross)}
            </div>
          </CardContent>
        </Card>

        <Card className="py-2">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">Depreciation FY {fy}</span>
              <TrendingDown className="h-4 w-4 text-orange-500" />
            </div>
            <div className="text-xl font-bold mt-1 text-orange-600 dark:text-orange-400">
              {inr(totals.depFy)}
            </div>
          </CardContent>
        </Card>

        <Card className="py-2">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">Closing Net Book Value</span>
              <ShieldAlert className="h-4 w-4 text-emerald-500" />
            </div>
            <div className="text-xl font-bold mt-1 text-emerald-600 dark:text-emerald-400">
              {inr(totals.nbv)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Table Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <CardTitle className="text-base">Asset-wise Depreciation Register</CardTitle>

            {/* Filters */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search code, name, category..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 h-9 text-xs"
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground"
                    title="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {categoryOptions.length > 0 && (
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-44 h-9 text-xs">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Categories</SelectItem>
                    {categoryOptions.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {hasFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearch("");
                    setCategoryFilter("ALL");
                  }}
                  className="h-9 px-2 text-xs text-muted-foreground hover:text-foreground"
                >
                  Reset
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <div className="max-h-[65vh] overflow-auto rounded-md border">
            <Table>
              <TableHeader className="bg-muted/50 sticky top-0 z-10">
                <TableRow>
                  <TableHead className="w-32">Code</TableHead>
                  <TableHead>Asset</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="w-20">Method</TableHead>
                  <TableHead className="text-right w-20">Rate %</TableHead>
                  <TableHead className="text-right">Gross Block</TableHead>
                  <TableHead className="text-right">Acc Dep Open</TableHead>
                  <TableHead className="text-right">Dep This FY</TableHead>
                  <TableHead className="text-right">Acc Dep Close</TableHead>
                  <TableHead className="text-right">NBV Closing</TableHead>
                  <TableHead className="text-right w-24">Rem. Life</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && <TableLoaderRow colSpan={11} />}

                {!isLoading && filteredRows.map((r) => (
                  <TableRow key={r.asset_id}>
                    <TableCell className="font-mono text-xs font-medium">{r.asset_code}</TableCell>
                    <TableCell className="font-medium">{r.asset_name}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">{r.category_name ?? "-"}</TableCell>
                    <TableCell>
                      <Badge variant={r.method === "SLM" ? "secondary" : r.method === "WDV" ? "outline" : "default"}>
                        {r.method}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {r.dep_rate > 0 ? `${r.dep_rate.toFixed(2)}%` : "-"}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">{inr(r.gross_block)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{inr(r.accum_dep_opening)}</TableCell>
                    <TableCell
                      className={`text-right font-mono text-xs font-medium ${
                        r.dep_this_fy > 0
                          ? "text-orange-600 dark:text-orange-400"
                          : "text-muted-foreground"
                      }`}
                    >
                      {inr(r.dep_this_fy)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">{inr(r.accum_dep_closing)}</TableCell>
                    <TableCell className="text-right font-mono text-xs font-semibold">{inr(r.nbv_closing)}</TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground">
                      {r.remaining_life_years != null
                        ? r.remaining_life_years > 0
                          ? `${r.remaining_life_years} yrs`
                          : "0 yrs (Expired)"
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))}

                {!isLoading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center text-muted-foreground py-10">
                      No capitalised assets found for FY {fy}.
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading && rows.length > 0 && filteredRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center text-muted-foreground py-10">
                      No assets match your search or filter criteria.
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading && filteredRows.length > 0 && (
                  <TableRow className="bg-muted/40 font-semibold border-t-2">
                    <TableCell colSpan={5}>
                      Totals ({filteredRows.length} {filteredRows.length === 1 ? "asset" : "assets"})
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">{inr(totals.gross)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{inr(totals.depOpen)}</TableCell>
                    <TableCell
                      className={`text-right font-mono text-xs ${
                        totals.depFy > 0
                          ? "text-orange-600 dark:text-orange-400"
                          : "text-muted-foreground"
                      }`}
                    >
                      {inr(totals.depFy)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">{inr(totals.depClose)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{inr(totals.nbv)}</TableCell>
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
